import type {
    KpiCardDataRequest,
    KpiCardState,
    KpiSource,
    KpiSourceDeps,
} from '../types';
import type { RegionGrain } from '~stores/kpi-envelope';
import {
    composeDownloadsCardData,
    normalizePlatformDownloads,
    DOWNLOADS_METRIC,
} from '~helpers/downloads-composition';
import type { DownloadsPlatform } from '~helpers/downloads-composition';
import { todayISO } from '../resolvers';
import { periodQuery } from '../periods';
import { FORM_DEFINITIONS } from '../form-definitions';
import { warnOnRegionGrainDrift } from './region-grain-drift';

/**
 * App-downloads KPI source. Unlike its siblings it is not a config over
 * `createEnvelopeSource`: the API serves downloads as two independent
 * per-platform envelopes, so this source fans out to both and composes the
 * result. That is the point of the split, not an accident of it: an App Store
 * outage no longer blanks Android's data, and the two calls have independent
 * failure modes.
 *
 * Both calls go through `fetchKpiEnvelope`, so they share every card's pacing,
 * dedup and abandon-on-abort plumbing, and both carry the card's abort signal.
 * The card-facing merge (and every degradation rule) lives in the pure
 * `downloads-composition` module.
 */

const LABEL = 'App downloads';

/** One endpoint per platform. The combined `analytics/downloads` is gone. */
const ENDPOINTS: Record<DownloadsPlatform, string> = {
    ios: 'analytics/downloads/ios',
    android: 'analytics/downloads/android',
};

/**
 * Both endpoints resolve region only down to a brand (Couche-Tard covers
 * Quebec, Circle K the rest of Canada), so a province selection is still
 * forwarded, and the drift guard checks the declared grain against BOTH
 * envelopes.
 */
const REGION_GRAIN: RegionGrain = 'brand';

/** Query params shared by the two calls, shaped like every envelope source's. */
const buildParams = (req: KpiCardDataRequest): Record<string, string> => {
    const params: Record<string, string> = {
        ...periodQuery(req.period),
        // Both granularities still carry `metrics.total`, so the per-platform
        // totals the chart shows come from the same field as the number card's.
        granularity: FORM_DEFINITIONS[req.form].granularity,
    };
    if (req.region.length > 0) {
        params.provinces = req.region.join(',');
    }
    if (req.compare) {
        params.compare = '1';
    }
    return params;
};

/**
 * Fetch and normalize one platform. Never rejects: a platform that fails
 * normalizes to a `failed` state so the sibling call's result still reaches the
 * card, which is what makes the two failure modes independent.
 */
const fetchPlatform = (
    platform: DownloadsPlatform,
    params: Record<string, string>,
    deps: KpiSourceDeps
) => {
    const endpoint = ENDPOINTS[platform];
    // Forward the card's abort signal only when the caller provided one, so a
    // stale request can be dropped on a filter change. A plain two-arg
    // call otherwise, so callers that never abort are unchanged.
    const envelope = deps.signal
        ? deps.analyticsStore.fetchKpiEnvelope(endpoint, params, deps.signal)
        : deps.analyticsStore.fetchKpiEnvelope(endpoint, params);
    return envelope
        .then((env) => {
            if (env) {
                // Catch a CMS/API drift on this KPI's region resolution before
                // it reaches production (see `warnOnRegionGrainDrift`).
                warnOnRegionGrainDrift(LABEL, endpoint, REGION_GRAIN, env);
            }
            return normalizePlatformDownloads(platform, env);
        })
        .catch(() => normalizePlatformDownloads(platform, null));
};

export const downloadsSource: KpiSource = {
    descriptor: {
        label: LABEL,
        metrics: [{ id: DOWNLOADS_METRIC, label: 'Downloads' }],
        forms: ['number', 'pie', 'downloads'],
        regionGrain: REGION_GRAIN,
    },

    async getCardData(
        req: KpiCardDataRequest,
        deps: KpiSourceDeps
    ): Promise<KpiCardState> {
        const params = buildParams(req);
        // One wave, two independent calls: neither waits on the other, and
        // neither can fail the other (see `fetchPlatform`).
        const [ios, android] = await Promise.all([
            fetchPlatform('ios', params, deps),
            fetchPlatform('android', params, deps),
        ]);
        // The clock lives here, not in the pure composition: the chart clips its
        // x-axis at today so a period running past today does not draw an empty
        // stretch of future.
        return composeDownloadsCardData(req, { ios, android }, todayISO());
    },
};
